import React from 'react';
import { Button, DatePicker } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { VirtualAccountDailySnapshotFilterType } from '../types/VirtualAccountDailySnapshotFilterType';
import { ChannelSelectFilter } from '../../../../../components/settlement/ChannelSelectFilter';
import { hasResource } from '../../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../../constants/resourceCodes';

interface VirtualAccountDailySnapshotFilterProps {
    filters: VirtualAccountDailySnapshotFilterType;
    setFilters: (filters: VirtualAccountDailySnapshotFilterType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    downloadLoading?: boolean;
    onDownload?: () => void;
}

const VirtualAccountDailySnapshotFilter: React.FC<VirtualAccountDailySnapshotFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
    downloadLoading,
    onDownload,
}) => {
    const canView = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_REALTIME_RECEIVABLE]);

    // 处理时间变化的函数 - 转换为YYYY-MM-DD格式
    const handleDateChange = (
        dates: [Dayjs | null, Dayjs | null] | null,
        dateStrings: [string, string]
    ) => {
        if (dates) {
            // 将 YYYY/MM/DD 格式转换为 YYYY-MM-DD 格式
            const formatDate = (date: string | null) => {
                if (!date) return null;
                return date.replace(/\//g, '-');
            };
            setFilters({
                ...filters,
                snapshotDateFrom: formatDate(dateStrings[0] || null),
                snapshotDateTo: formatDate(dateStrings[1] || null)
            });
        } else {
            setFilters({
                ...filters,
                snapshotDateFrom: null,
                snapshotDateTo: null
            });
        }
    };

    // 获取日期范围值用于显示 - 将 YYYY-MM-DD 转换为 Dayjs 对象
    const getDateValue = (): [Dayjs | null, Dayjs | null] | null => {
        if (filters.snapshotDateFrom || filters.snapshotDateTo) {
            return [
                filters.snapshotDateFrom ? dayjs(filters.snapshotDateFrom) : null,
                filters.snapshotDateTo ? dayjs(filters.snapshotDateTo) : null
            ];
        }
        return null;
    };

    return (
        <div className="mb-6">
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <ChannelSelectFilter
                    stacked
                    label="客户名称"
                    value={filters.clientId != null ? String(filters.clientId) : undefined}
                    onChange={(value: string | undefined) => setFilters({...filters, clientId: value ? Number(value) : null})}
                />
                <div className="flex flex-col">
                    <span className="block text-sm font-medium mb-1">快照日期</span>
                    <DatePicker.RangePicker
                        value={getDateValue()}
                        onChange={handleDateChange}
                        className="w-full h-8"
                        placeholder={['开始日期', '结束日期']}
                        format="YYYY/MM/DD"
                    />
                </div>
            </div>

            <div className="mb-4 flex justify-start">
                <div className="space-x-4">
                    {canView && (
                        <Button type="primary" onClick={handleFilter}>
                            Filter
                        </Button>
                    )}
                    {canView && (
                        <Button onClick={handleResetFilters}>
                            Reset
                        </Button>
                    )}
                    {canView && onDownload && (
                        <Button icon={<DownloadOutlined />} loading={downloadLoading} onClick={onDownload}>
                            导出数据
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VirtualAccountDailySnapshotFilter;
